import {
  IpmErrorBoundary,
  reportIpmAction,
  reportIpmVisible,
  type IpmMetricTags,
  useIpmRenderTiming,
} from '@github-ui/ipm-telemetry'
import {type FormEvent, type ReactNode, Profiler, useEffect, useId, useMemo, useState} from 'react'

import {GitHubAvatar} from '@github-ui/github-avatar'
import {CopilotIcon} from '@primer/octicons-react'
import {Button, Checkbox, Label, TextInput} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'

import styles from './CopilotBudgetRequestApprovals.module.css'

export interface CopilotBudgetRequestApprovalRow {
  /** MemberFeatureRequest id; used to build the `budget_requests[ID][...]` params. */
  id: string
  /** Requesting member's login, shown next to their avatar. */
  requesterLogin: string
  /** Requester avatar URL. */
  avatarUrl: string
  /** Pre-formatted dollar amount (e.g. "100.00") used as the amount field's default. */
  amountValue: string
  /** Relative duration only (e.g. "2 days"), rendered as "Requested {requestedAgoLabel} ago". */
  requestedAgoLabel: string
}

/** Which admin surface is rendering the approvals list, encoded into the `surface` telemetry tag so the IPM dashboard can pivot enterprise vs org (IPM-697). */
export type CopilotBudgetRequestApprovalsDisplayLocation = 'organization' | 'enterprise'

export interface CopilotBudgetRequestApprovalsProps {
  /** POST target for the approvals form (Rails `budget_request_approvals#create`). */
  formAction: string
  /** URL of the "Back to member requests" CTA. */
  backUrl: string
  /** Rails CSRF token, rendered as the `authenticity_token` hidden field. */
  csrfToken: string
  /** Pending budget requests to review. */
  budgetRequests: CopilotBudgetRequestApprovalRow[]
  /** Admin surface (org vs enterprise) rendering this list; encoded into the `surface` tag. */
  displayLocation: CopilotBudgetRequestApprovalsDisplayLocation
}

interface ApprovalsFormProps extends Omit<CopilotBudgetRequestApprovalsProps, 'displayLocation'> {
  telemetryTags: IpmMetricTags
}

/**
 * Org admin review page for pending "additional Copilot budget" requests
 * (IPM-474, Scene 3). Ported from the Rails view
 * `settings/organization/member_feature_requests/budget_requests/index`.
 *
 * The form still POSTs to Rails (`budget_request_approvals#create`); React only
 * owns the selection state. The original ERB disabled the submit button with a
 * CSS-only `:has()` rule, which left the button focusable but operable, so it
 * could submit with nothing selected. A native `disabled` attribute would fix
 * that but removes the button from the tab order, so assistive-tech users can't
 * discover it or learn why it's unavailable. Instead we keep the button
 * discoverable with `inactive` (`aria-disabled="true"`, still focusable), block
 * the POST at submit time when nothing is selected, and announce the reason via
 * a polite live region.
 */
export function CopilotBudgetRequestApprovals({
  formAction,
  backUrl,
  csrfToken,
  budgetRequests,
  displayLocation,
}: CopilotBudgetRequestApprovalsProps) {
  const telemetryTags = useMemo<IpmMetricTags>(
    () => ({
      package: 'in-product-targeting',
      component: 'CopilotBudgetRequestApprovals',
      // `display_location` identifies the feature/page (shared with the backend
      // interaction metric). The org-vs-enterprise scope is encoded in `surface`
      // so the IPM dashboard can pivot on it (IPM-697).
      surface: `copilot_budget_request_approvals_${displayLocation}`,
      display_location: 'budget_requests',
      category: 'copilot_budget_request_approvals',
      nudge_id: 'copilot_premium_request_budget',
      variant: budgetRequests.length === 0 ? 'empty' : 'pending_requests',
    }),
    [budgetRequests.length, displayLocation],
  )
  const renderTiming = useIpmRenderTiming(telemetryTags)

  useEffect(() => {
    reportIpmVisible(telemetryTags)
  }, [telemetryTags])

  function renderWithTelemetry(children: ReactNode) {
    const content = !renderTiming.isEnabled ? (
      children
    ) : (
      <Profiler id="CopilotBudgetRequestApprovals" onRender={renderTiming.onRender}>
        {children}
      </Profiler>
    )

    return (
      <IpmErrorBoundary tags={telemetryTags} boundaryName="CopilotBudgetRequestApprovals">
        {content}
      </IpmErrorBoundary>
    )
  }

  if (budgetRequests.length === 0) {
    return renderWithTelemetry(
      <>
        <Blankslate data-testid="budget-requests-blankslate">
          <Blankslate.Visual>
            <CopilotIcon size={24} />
          </Blankslate.Visual>
          <Blankslate.Heading>No pending budget requests</Blankslate.Heading>
          <Blankslate.Description>
            When members request additional Copilot budget, their requests will appear here.
          </Blankslate.Description>
        </Blankslate>

        <div className={styles.backRow}>
          <Button
            as="a"
            href={backUrl}
            data-testid="budget-requests-back-cta"
            onClick={() => reportIpmAction('click', {...telemetryTags, variant: 'back_cta'})}
          >
            Back to member requests
          </Button>
        </div>
      </>,
    )
  }

  return renderWithTelemetry(
    <ApprovalsForm
      formAction={formAction}
      backUrl={backUrl}
      csrfToken={csrfToken}
      budgetRequests={budgetRequests}
      telemetryTags={telemetryTags}
    />,
  )
}

function ApprovalsForm({formAction, backUrl, csrfToken, budgetRequests, telemetryTags}: ApprovalsFormProps) {
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(() => new Set())
  const [showSelectionHint, setShowSelectionHint] = useState(false)
  const selectionHintId = useId()

  const allSelected = selectedIds.size === budgetRequests.length
  const someSelected = selectedIds.size > 0 && !allSelected
  const noneSelected = selectedIds.size === 0

  const idsInOrder = useMemo(() => budgetRequests.map(request => request.id), [budgetRequests])

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    if (noneSelected) {
      event.preventDefault()
      setShowSelectionHint(true)
      reportIpmAction('error', {...telemetryTags, variant: 'submit_without_selection'}, {samplingProbability: 1})
      return
    }

    reportIpmAction('submit', {...telemetryTags, variant: 'approve_submit'})
  }

  function toggleRow(id: string, checked: boolean) {
    setSelectedIds(previous => {
      const next = new Set(previous)
      if (checked) {
        next.add(id)
      } else {
        next.delete(id)
      }
      return next
    })
  }

  function toggleAll(checked: boolean) {
    setSelectedIds(checked ? new Set(idsInOrder) : new Set())
  }

  return (
    <form action={formAction} method="post" onSubmit={handleSubmit}>
      {/* eslint-disable-next-line github/authenticity-token -- server-provided Rails token forwarded as the form's authenticity_token */}
      <input type="hidden" name="authenticity_token" value={csrfToken} data-testid="budget-requests-csrf" />

      {/* Forward the interaction's page + element so the backend interaction metric
          groups on the same {surface, display_location} the frontend emits. */}
      {telemetryTags.display_location && (
        <input type="hidden" name="display_location" value={telemetryTags.display_location} />
      )}
      {telemetryTags.surface && <input type="hidden" name="surface" value={telemetryTags.surface} />}

      <div className={styles.toolbar}>
        <span className={styles.selectAllLabel}>
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected}
            onChange={event => toggleAll(event.currentTarget.checked)}
            aria-label="Select all requests"
            data-testid="budget-requests-select-all"
          />
          <span className="color-fg-muted" data-testid="budget-requests-selected-status">
            <span data-testid="budget-requests-selected-count">{selectedIds.size}</span> selected
          </span>
        </span>
        <span className="flex-auto" />
        <Button
          type="submit"
          variant="primary"
          inactive={noneSelected}
          aria-disabled={noneSelected || undefined}
          aria-describedby={selectionHintId}
          data-testid="approve-budget-requests-submit"
        >
          Approve and increase
        </Button>
      </div>

      <span
        id={selectionHintId}
        role="status"
        aria-live="polite"
        className={noneSelected && showSelectionHint ? 'color-fg-danger f6 d-block mb-2' : 'sr-only'}
        data-testid="budget-requests-selection-hint"
      >
        {noneSelected && showSelectionHint ? 'Select at least one request to approve.' : ''}
      </span>

      <div className={`Box ${styles.box}`}>
        {budgetRequests.map(request => {
          const checked = selectedIds.has(request.id)
          return (
            <div key={request.id} className="Box-row" data-testid="budget-request-row">
              <div className={styles.requestRow}>
                <div className={styles.checkboxAndAvatar}>
                  <Checkbox
                    name={`budget_requests[${request.id}][approve]`}
                    value="1"
                    checked={checked}
                    onChange={event => toggleRow(request.id, event.currentTarget.checked)}
                    aria-label={`Approve request from ${request.requesterLogin}`}
                  />
                  <span className={styles.avatar}>
                    <GitHubAvatar src={request.avatarUrl} size={32} alt="" />
                  </span>
                </div>
                <div className={styles.requestMeta}>
                  <div className={styles.titleAndTimestamp}>
                    <span className="text-bold" data-testid="budget-request-requester">
                      {request.requesterLogin}
                    </span>
                    <span className="color-fg-muted f6" data-testid="budget-request-timestamp">
                      Requested {request.requestedAgoLabel} ago
                    </span>
                  </div>
                  <div className={styles.pillAndField}>
                    <span className={styles.pill}>
                      <Label variant="secondary">Organization budget</Label>
                    </span>
                    <div className={styles.amountField}>
                      <span className="color-fg-muted mr-1">$</span>
                      <TextInput
                        type="number"
                        name={`budget_requests[${request.id}][amount_dollars]`}
                        defaultValue={request.amountValue}
                        min="0"
                        step="0.01"
                        inputMode="decimal"
                        size="small"
                        className={styles.amountInput}
                        data-testid="budget-request-amount"
                        aria-label={`Approved amount in dollars for ${request.requesterLogin}`}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <div className={styles.backRow}>
        <Button
          as="a"
          href={backUrl}
          data-testid="budget-requests-back-cta"
          onClick={() => reportIpmAction('click', {...telemetryTags, variant: 'back_cta'})}
        >
          Back to member requests
        </Button>
      </div>
    </form>
  )
}
